import axios from 'axios';
import {
  ADMIN_VERIFY_APPLICATION_URL,
  ASSIGN_USER_APPLICATION_URL,
  CREATE_ENTITY_APPLICATION_ONLY_URL,
  CREATE_JOURNAL_URL,
  GET_ASSIGNED_USERS_FOR_APPLICATION_URL, GET_APPLICATION_DOCUMENTS_URL,
  GET_APPLICATION_FORMS_URL, GET_ALL_AUDIT_LOGS_URL, GET_FORM_DETAILS_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL, GET_FULL_ENTITY_CONFIG_URL, GET_JOURNAL_URL,
  MANUAL_VERIFY_ADDITIONAL_DOCUMENT_URL,
  MANUAL_VERIFY_SECTION_QUESTION_API_URL,
  GET_PROJECT_BINDINGS,
  GET_USERS,
  LIST_ENTITY_PROFILES_URL,
  SUBMIT_APPLICATION_FORM_ANSWERS_URL,
  UNASSIGN_USER_APPLICATION_URL,
  UPLOAD_ADDITIONAL_DOCUMENTS_URL, VIEW_ENTITY_TYPE_URL,
  get_auth_headers, get_request_config,
} from '@/utils/system_constants/constant.js';

const response_cache = new Map();
const in_flight_requests = new Map();
const cached_get_requests = new Map([
  [VIEW_ENTITY_TYPE_URL, (params) => axios.get(VIEW_ENTITY_TYPE_URL, get_request_config({ params }))],
  [GET_FULL_ENTITY_CONFIG_URL, (params) => axios.get(GET_FULL_ENTITY_CONFIG_URL, get_request_config({ params }))],
  [GET_FULL_CONFIG_BY_ENTITY_TYPE_URL, (params) => axios.get(GET_FULL_CONFIG_BY_ENTITY_TYPE_URL, get_request_config({ params }))],
  [GET_FORM_DETAILS_URL, (params) => axios.get(GET_FORM_DETAILS_URL, get_request_config({ params }))],
  [GET_APPLICATION_FORMS_URL, (params) => axios.get(GET_APPLICATION_FORMS_URL, get_request_config({ params }))],
  [GET_APPLICATION_DOCUMENTS_URL, (params) => axios.get(GET_APPLICATION_DOCUMENTS_URL, get_request_config({ params }))],
  [GET_JOURNAL_URL, (params) => axios.get(GET_JOURNAL_URL, get_request_config({ params }))],
  [GET_ASSIGNED_USERS_FOR_APPLICATION_URL, (params) => axios.get(GET_ASSIGNED_USERS_FOR_APPLICATION_URL, get_request_config({ params }))],
  [GET_ALL_AUDIT_LOGS_URL, (params) => axios.get(GET_ALL_AUDIT_LOGS_URL, get_request_config({ params }))],
  [LIST_ENTITY_PROFILES_URL, (params) => axios.get(LIST_ENTITY_PROFILES_URL, get_request_config({ params }))],
  [GET_USERS, (params) => axios.get(GET_USERS, get_request_config({ params }))],
]);

function request_cache_key(url, params = {}) {
  return `${url}?${JSON.stringify(params || {})}`;
}

function request_cached_get(url, params) {
  const request = cached_get_requests.get(url);
  if (!request) {
    throw new TypeError('Unsupported cached GET URL');
  }

  return request(params);
}

async function cached_get(url, params = {}, { force = false } = {}) {
  const cache_key = request_cache_key(url, params);
  if (!force && response_cache.has(cache_key)) {
    return response_cache.get(cache_key);
  }

  if (!force && in_flight_requests.has(cache_key)) {
    return in_flight_requests.get(cache_key);
  }

  const request = request_cached_get(url, params).then((response) => {
    response_cache.set(cache_key, response);
    return response;
  }).finally(() => {
    in_flight_requests.delete(cache_key);
  });

  in_flight_requests.set(cache_key, request);
  return request;
}

function clear_cached_request(url, params = null) {
  if (!params) {
    [...response_cache.keys()]
      .filter((key) => key.startsWith(`${url}?`))
      .forEach((key) => {
        response_cache.delete(key);
      });
    return;
  }

  response_cache.delete(request_cache_key(url, params));
}

function parse_json_string(value, fallback = {}) {
  try { return JSON.parse(value); }
  catch { return fallback; }
}
function response_payload(response) {
  if (response?.data !== undefined && response?.data !== null) return response.data;
  return response ?? {};
}
function parse_nested_string(value) {
  return typeof value === 'string' ? parse_json_string(value) : value;
}
function parse_response_data(response) {
  const raw_data = response_payload(response);
  if (typeof raw_data !== 'string') return raw_data;
  return parse_nested_string(parse_json_string(raw_data));
}
function to_array(value) {
  return Array.isArray(value) ? value : [];
}
export const fetch_requestor_project_bindings = async (project_id) => {
  const response = await axios.get(
    GET_PROJECT_BINDINGS,
    get_request_config({ params: { project_id } })
  );
  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed).filter((binding) => binding?.is_active !== false);
};
export const fetch_requestor_entity_types = async (params = {}) => {
  const response = await cached_get(VIEW_ENTITY_TYPE_URL, params);
  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const fetch_requestor_full_config = async (entity_config_id) => {
  const response = await cached_get(GET_FULL_ENTITY_CONFIG_URL, { entity_config_id });
  return parse_response_data(response)?.data || {};
};
export const fetch_requestor_full_config_by_entity_type = async (entity_type_id, params = {}) => {
  const response = await cached_get(GET_FULL_CONFIG_BY_ENTITY_TYPE_URL, { ...params, entity_type_id });
  return parse_response_data(response)?.data || {};
};
export const fetch_requestor_form_details = async (form_id) => {
  const response = await cached_get(GET_FORM_DETAILS_URL, { form_id });
  return parse_response_data(response)?.data || {};
};

export const create_requestor_case = async (payload = {}) => {
  const response = await axios.post(
    CREATE_ENTITY_APPLICATION_ONLY_URL,
    payload,
    get_request_config()
  );
  clear_cached_request(LIST_ENTITY_PROFILES_URL);
  return parse_response_data(response);
};

export const fetch_requestor_application_forms = async (application_id) => {
  const response = await cached_get(GET_APPLICATION_FORMS_URL, { application_id });
  const parsed = parse_response_data(response);
  const payload = parse_response_data(parsed?.data || parsed);
  return to_array(payload?.data || payload);
};

export const fetch_requestor_application_documents = async (application_id) => {
  const response = await cached_get(GET_APPLICATION_DOCUMENTS_URL, { application_id });
  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const upload_requestor_application_documents = async ({
  application_id,
  files = [],
  document_names = [],
  on_upload_progress = null,
} = {}) => {
  const form_data = new FormData();
  form_data.append('application_id', application_id);

  to_array(files).forEach((file, index) => {
    const document_name = to_array(document_names).at(index);
    form_data.append('files', file);
    form_data.append('document_names', document_name || file?.name || 'Document');
  });

  const response = await axios.post(
    UPLOAD_ADDITIONAL_DOCUMENTS_URL,
    form_data,
    {
      withCredentials: true,
      onUploadProgress: on_upload_progress || undefined,
      headers: {
        ...get_auth_headers(),
      },
    }
  );

  clear_cached_request(GET_APPLICATION_DOCUMENTS_URL, { application_id });

  return parse_response_data(response);
};

export const review_requestor_application_document = async ({
  additional_document_id,
  is_verified,
  verification_comment = '',
} = {}) => {
  const response = await axios.post(
    MANUAL_VERIFY_ADDITIONAL_DOCUMENT_URL,
    {
      additional_document_id,
      is_verified,
      verification_comment,
    },
    get_request_config()
  );

  return parse_response_data(response);
};

export const review_requestor_application_question_document = async ({
  application_form_id,
  section_id,
  question_id,
  is_verified,
  verification_comment = '',
} = {}) => {
  const response = await axios.post(
    MANUAL_VERIFY_SECTION_QUESTION_API_URL,
    {
      application_form_id,
      section_id,
      question_id,
      is_verified,
      verification_comment,
    },
    get_request_config()
  );

  return parse_response_data(response);
};

export const fetch_requestor_application_notes = async (application_id) => {
  const response = await cached_get(GET_JOURNAL_URL, { application_id });
  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const fetch_requestor_application_journals = fetch_requestor_application_notes;

export const create_requestor_application_note = async ({
  application_id,
  user_id,
  journal_description,
  hours_spent = '0.00',
  journal_date = null,
  evidence_files = [],
  evidence_names = [],
} = {}) => {
  const form_data = new FormData();
  form_data.append('application_id', application_id);
  form_data.append('user_id', user_id);
  form_data.append('journal_description', journal_description);
  form_data.append('hours_spent', hours_spent);
  if (journal_date) {
    form_data.append('journal_date', journal_date);
  }
  to_array(evidence_files).forEach((file, index) => {
    if (!(typeof File !== 'undefined' && file instanceof File)) {
      return;
    }

    const evidence_name = to_array(evidence_names).at(index);
    form_data.append('file_url', file);
    form_data.append('document_name', evidence_name || file.name || `Evidence ${index + 1}`);
  });

  const response = await axios.post(
    CREATE_JOURNAL_URL,
    form_data,
    {
      withCredentials: true,
      headers: {
        ...get_auth_headers(),
      },
    }
  );

  clear_cached_request(GET_JOURNAL_URL, { application_id });

  return parse_response_data(response);
};

export const create_requestor_application_journal = create_requestor_application_note;

export const fetch_reassignable_users = async (params = {}) => {
  const response = await cached_get(GET_USERS, {
    page: 1,
    page_size: 500,
    ...params,
  });
  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const assign_requestor_application_users = async ({
  application_id,
  assignments = [],
  note = '',
} = {}) => {
  const requests = to_array(assignments).map((assignment) =>
    axios.post(
      ASSIGN_USER_APPLICATION_URL,
      {
        application_id,
        user_id: assignment.user_id,
        role_id: assignment.role_id,
        note,
      },
      get_request_config()
    )
  );

  const responses = await Promise.all(requests);
  clear_cached_request(GET_ASSIGNED_USERS_FOR_APPLICATION_URL);
  return responses.map(parse_response_data);
};

export const unassign_requestor_application_users = async ({
  application_id,
  assignments = [],
} = {}) => {
  const requests = to_array(assignments).map((assignment) =>
    axios.post(
      UNASSIGN_USER_APPLICATION_URL,
      {
        application_id,
        user_id: assignment.user_id,
        assignment_id: assignment.assignment_id,
      },
      get_request_config()
    )
  );

  const responses = await Promise.all(requests);
  clear_cached_request(GET_ASSIGNED_USERS_FOR_APPLICATION_URL);
  return responses.map(parse_response_data);
};

export const fetch_requestor_application_assigned_users = async (application_id) => {
  const response = await cached_get(GET_ASSIGNED_USERS_FOR_APPLICATION_URL, { application_id });
  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const update_requestor_application_status = async (payload = {}) => {
  const response = await axios.post(
    ADMIN_VERIFY_APPLICATION_URL,
    payload,
    get_request_config()
  );
  return parse_response_data(response);
};

export const fetch_requestor_application_audit_logs = async (params = {}) => {
  const response = await cached_get(GET_ALL_AUDIT_LOGS_URL, params);
  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const submit_requestor_form_answers = async (payload = {}) => {
  const answers = to_array(payload?.answers);
  const file_answers = answers.filter((answer) =>
    typeof File !== 'undefined' && answer?.file instanceof File
  );

  if (file_answers.length) {
    const form_data = new FormData();
    form_data.append('application_form_id', payload.application_form_id);
    if (payload.application_id) {
      form_data.append('application_id', payload.application_id);
    }
    if (payload.current_section_index !== undefined && payload.current_section_index !== null) {
      form_data.append('current_section_index', payload.current_section_index);
    }

    const serialized_answers = answers.map((answer) => {
      const { file, ...serializable_answer } = answer;
      return {
        ...serializable_answer,
        answer_text: file instanceof File ? file.name : serializable_answer.answer_text,
      };
    });

    form_data.append('answers', JSON.stringify(serialized_answers));
    file_answers.forEach((answer) => {
      const file_key = answer.section_question_id || answer.question_id;
      if (file_key) {
        form_data.append(`file_${file_key}`, answer.file);
      }
    });

    const response = await axios.post(
      SUBMIT_APPLICATION_FORM_ANSWERS_URL,
      form_data,
      {
        withCredentials: true,
        headers: {
          ...get_auth_headers(),
        },
      }
    );
    clear_cached_request(GET_APPLICATION_FORMS_URL);
    clear_cached_request(GET_APPLICATION_DOCUMENTS_URL);
    return parse_response_data(response);
  }

  const response = await axios.post(
    SUBMIT_APPLICATION_FORM_ANSWERS_URL,
    payload,
    get_request_config()
  );
  clear_cached_request(GET_APPLICATION_FORMS_URL);
  return parse_response_data(response);
};

export const fetch_requestor_entity_profiles = async (params = {}, options = {}) => {
  const response = await cached_get(LIST_ENTITY_PROFILES_URL, params, options);
  const parsed = parse_response_data(response);
  return {
    data: to_array(parsed?.data || parsed),
    field_labels: parsed?.field_labels || {},
    pagination: parsed?.pagination || {},
  };
};
